import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import LicenseRestriction from "views/Components/License";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import API from "lib/api/API2";
import SaveRequestModal from "views/Components/SaveRequestModal";
import { compileParams } from "views/Components/ServerTable";

export const Root = styled(Box)(({ theme }) => ({
  flex: "0 0 auto",
  marginBottom: 0,
  marginLeft: "auto",
  marginRight: 0,
  marginTop: 5,
  whiteSpace: "nowrap",
}));

const MoreItems = ({
  activeFields,
  controls,
  displayMessage,
  gotoScheduledReports,
  orgId,
  reset,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const handleExport = async (downloadType) => {
    setIsExporting(true);
    const params = [
      // TODO handle `search` as well
      ...Object.values(controls).filter((c) => c.field !== "search"),
      {
        field: "outputFields",
        value: activeFields.filter((f) => f !== "ownersExpanded"),
      },
      {
        field: "outputType",
        value: downloadType,
      },
      {
        field: "page",
        value: "1:5000",
      },
    ];
    try {
      const api = new API();
      const res = await api.get("findingexport", compileParams(params), []);
      if (!res.message) {
        throw new Error("Missing `message` from response");
      }
      window.open(res.message, "_blank");
    } catch (err) {
      console.error(err);
      displayMessage({
        severity: "error",
        message: `Failed to export report: ${err}`,
        noAutoHide: true,
      });
    } finally {
      setAnchorEl(null);
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setAnchorEl(null);
    reset();
  };

  const handleSavePreset = () => {
    setAnchorEl(null);
    setSaveOpen(true);
  };

  return (
    <Root>
      <IconButton
        aria-controls="more-menu"
        datacy="moreMenuItemsBtn"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        title="More Options"
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        disableScrollLock={true}
      >
        <MenuItem onClick={handleReset}>Reset Search</MenuItem>
        <MenuItem onClick={handleSavePreset}>Save Preset</MenuItem>
        <LicenseRestriction requires="report.create">
          <MenuItem
            onClick={() => gotoScheduledReports({ orgId })}
            datacy="scheduledReportsPageBtn"
          >
            Scheduled Reports
          </MenuItem>
        </LicenseRestriction>
        <Divider orientation="horizontal" />
        <MenuItem onClick={() => handleExport("csv")} disabled={isExporting}>
          Export CSV
        </MenuItem>
        <MenuItem onClick={() => handleExport("jsonl")} disabled={isExporting}>
          Export JSON-L
        </MenuItem>
      </Menu>
      <SaveRequestModal
        activeFields={activeFields}
        controls={controls}
        displayMessage={displayMessage}
        onClose={() => setSaveOpen(false)}
        open={saveOpen}
      />
    </Root>
  );
};

MoreItems.propTypes = {
  activeFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  controls: PropTypes.object.isRequired,
  displayMessage: PropTypes.func.isRequired,
  gotoScheduledReports: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  reset: PropTypes.func.isRequired,
};

export default MoreItems;
